import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { CLIENT_HEADER } from 'src/constants';
import { captureException } from 'src/observability';
import {
  MAX_RETRIES,
  RETRY_STATUS,
  computeRetryDelayMs,
  isIdempotentMethod,
  sleep,
} from './retryAfter';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    // avoid authorization header for SSR support
    'Content-Type': 'application/json',
    accept: 'application/json',
    // Stamp web-client traffic on the legacy axios transport too (MKT-455 / TG-2).
    [CLIENT_HEADER.NAME]: CLIENT_HEADER.WEB,
  },
});

// Per-request retry bookkeeping stashed on the config so it survives re-issues.
// `retryOn503` is set by callers (via `request`) to opt idempotent non-GET reads
// into the retry — e.g. ES reads that POST a large query-params body.
interface RetryableConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
  __retryStart?: number;
  retryOn503?: boolean;
}

/**
 * Only retry shed 503s on idempotent reads: GET/HEAD/OPTIONS by default, plus
 * any request the caller explicitly flagged with `retryOn503`. Mutations and
 * other statuses are never auto-retried.
 */
const shouldRetry503 = (error: AxiosError): boolean => {
  if (error.response?.status !== RETRY_STATUS) return false;
  const config = error.config as RetryableConfig | undefined;
  return isIdempotentMethod(config?.method) || config?.retryOn503 === true;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const isUnauthorizedError = error.response?.status === 401;

    if (error.response?.status === 500) captureException(error);
    if (error?.code === 'ECONNABORTED') captureException(error);

    // Bounded retry for shed 503s: honor Retry-After + jitter, capped by both a
    // retry count and a total-time budget. On give-up we fall through to the
    // normal reject so the existing error UI still surfaces the body.
    if (shouldRetry503(error) && error.config) {
      const config = error.config as RetryableConfig;
      config.__retryStart = config.__retryStart ?? Date.now();
      config.__retryCount = (config.__retryCount ?? 0) + 1;

      const elapsed = Date.now() - config.__retryStart;
      const retryAfter = error.response?.headers?.['retry-after'];
      const delay = computeRetryDelayMs(retryAfter, elapsed);

      if (config.__retryCount <= MAX_RETRIES && delay != null) {
        await sleep(delay);
        return axiosInstance.request(config);
      }
    }

    // Reject all errors — 401 logout is handled by AuthorizedLayout's
    // auto-logout effect, not here. The interceptor redirect caused race
    // conditions with 403 account-blocked flows.
    return Promise.reject(error);
  }
);

export default axiosInstance;
